import React, { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components";

import defaultLogo from "../../../assets/images/default-logo.png";
import { LOGO_MAX_WIDTH, MEDIA_URL } from "../../../constants";
import Button from "../../Button";
import UploadPreviewModal from "../../UploadPreviewModal";
import { useUserState } from "context/User";

const logoRatioOptions = [
  { ratio: 1, label: "1:1", example: { width: 16, height: 16 } },
  { ratio: 2, label: "2:1", example: { width: 32, height: 16 } },
];

const Image = styled.img`
  background: ${({ bg }) => bg};
  background-size: cover;
`;

const LogoUploadFormField = ({
  field,
  form,
  attachMediaURL,
  background,
  onLogoDelete,
}) => {
  const { permissions, currentProfile } = useUserState();
  const canEditLogo = permissions?.canEditLogo;

  const [image, setImage] = useState();
  const [isCropperModalOpen, setIsCropperModalOpen] = useState(false);
  const blobUrlRef = useRef();

  useEffect(
    () => () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    },
    [],
  );

  const handleInputChange = useCallback(
    e => {
      if (!canEditLogo) {
        return;
      }
      e.preventDefault();
      if (!e.target.files?.length) return;
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const url = URL.createObjectURL(e.target.files[0]);
      blobUrlRef.current = url;
      setImage(url);
      setIsCropperModalOpen(true);
      e.target.value = "";
    },
    [canEditLogo],
  );

  const handleLogoDelete = () => {
    if (!canEditLogo) {
      return;
    }
    form.setFieldValue(field.name, "");
    setImage(undefined);
    onLogoDelete();
  };

  const setCroppedImage = useCallback(
    croppedImage => {
      setImage(croppedImage);

      if (!!croppedImage) {
        form.setFieldValue(field.name, croppedImage);
      }
    },
    [form],
  );

  const handleCropperClose = useCallback(() => {
    setIsCropperModalOpen(false);
  }, []);

  const handleCropperSave = useCallback(
    cropped => {
      setCroppedImage(cropped);
      setIsCropperModalOpen(false);
    },
    [setCroppedImage],
  );

  const handleCropperCancel = useCallback(() => {
    setCroppedImage(undefined);
  }, [setCroppedImage]);

  return (
    <>
      <div className="flex flex-col items-start md:flex-row md:items-center">
        <div className="ml-1 mb-1 h-20 w-40 overflow-hidden">
          {image && (
            <Image
              bg={background}
              src={image}
              alt=""
              className="h-full rounded-md p-1"
            />
          )}
          {!image && field.value && (
            <Image
              bg={background}
              alt=""
              src={`${
                attachMediaURL ? `${MEDIA_URL}${field.value}` : field.value
              }`}
              className="h-full rounded-md p-1"
            />
          )}
          {!image && !field.value && (
            <Image
              bg={background}
              alt=""
              src={defaultLogo}
              className="h-full rounded-md p-1"
            />
          )}
        </div>
        <div className="flex flex-row items-start">
          <label
            className={`ml-1 ${canEditLogo ? "cursor-pointer" : ""}`}
            htmlFor={field.name}
          >
            <Button
              className="pointer-events-none"
              htmlFor={field.name}
              variant="white"
              size="small"
              type="button"
              disabled={!canEditLogo}
            >
              Change
            </Button>
          </label>
          <input
            type="file"
            accept=".png, .jpg, .jpeg"
            id={field.name}
            className="invisible w-0 h-0 absolute left-0 top-0"
            onChange={handleInputChange}
            data-testid="logo-upload-input"
            disabled={!canEditLogo}
          />
          {(image || field.value) && (
            <Button
              onClick={handleLogoDelete}
              className="ml-4"
              variant="red"
              size="small"
              type="button"
              disabled={!canEditLogo}
            >
              Delete
            </Button>
          )}
        </div>
      </div>
      {isCropperModalOpen && currentProfile && (
        <UploadPreviewModal
          isOpen={isCropperModalOpen}
          onClose={handleCropperClose}
          variant="logo"
          image={image}
          aspect={1}
          format="png"
          maxWidth={LOGO_MAX_WIDTH}
          onSave={handleCropperSave}
          onCancel={handleCropperCancel}
          profile={currentProfile}
          ratioOptions={logoRatioOptions}
        />
      )}
    </>
  );
};

export default LogoUploadFormField;
